/**
 * Drill player roles. The drill canvas extends Josh's home/away model
 * with a richer set of roles + colours so a coach can express:
 *   - Attackers / Defenders (the basic two)
 *   - Neutrals / All-time (always-on-the-ball-team players in SSGs)
 *   - Bumpers (touchline/end-line servers)
 *   - Servers / Coach (whistle, distributing balls)
 *   - Custom roles (coach-named, custom colour)
 *
 * Each placed player is a DrillRolePlayer — stored as a separate
 * overlay layer alongside cones, NOT in TacticsLayout.homeSpots/awaySpots.
 * Persists into drill_spec under a custom `rolePlayers` key.
 */

export interface PlayerRoleDef {
  id: string;
  label: string;
  color: string;
  /** Short glyph used inside the player token (when there's no jersey). */
  glyph?: string;
  /** Whether to show on legend by default. */
  defaultOn?: boolean;
}

export const DEFAULT_ROLES: PlayerRoleDef[] = [
  { id: 'attacker',  label: 'Attacker',  color: '#E74C3C', glyph: 'A', defaultOn: true },
  { id: 'defender',  label: 'Defender',  color: '#3498DB', glyph: 'D', defaultOn: true },
  { id: 'neutral',   label: 'Neutral',   color: '#F1C40F', glyph: 'N' },
  { id: 'bumper',    label: 'Bumper',    color: '#9B59B6', glyph: 'B' },
  { id: 'server',    label: 'Server',    color: '#1ABC9C', glyph: 'S' },
  { id: 'coach',     label: 'Coach',     color: '#34495E', glyph: 'C' },
  { id: 'goalkeeper',label: 'Goalkeeper',color: '#F39C12', glyph: 'GK' },
  // Ball isn't a player but it's the same kind of placeable on the
  // canvas — gives a clean single-tray UX instead of forcing a
  // separate "ball tool" panel. Rendered as a white token.
  { id: 'ball',      label: 'Ball',      color: '#FFFFFF', glyph: '⚽' },
];

export interface DrillRolePlayer {
  id: string;
  roleId: string;
  /** Normalised [0,1] pitch coords, same convention as DrillCone. */
  position: [number, number];
  /** Display label — usually a jersey number; falls back to the
   *  role's glyph if absent. */
  label?: string;
  /** Optional per-token colour override — when set, the token renders
   *  with this hex instead of the role's default. Lets the coach pick a
   *  bib colour or mark a key player without changing role semantics. */
  color?: string;
}

/** Standard kit colours offered in the per-token colour picker. The
 *  set is intentionally small — coaches pick the colour that matches
 *  the bibs / kits / cones they're using on the pitch. */
export const ROLE_COLOR_SWATCHES: string[] = [
  '#E74C3C', // red (default attacker)
  '#3498DB', // blue (default defender)
  '#27AE60', // green
  '#F1C40F', // yellow
  '#F39C12', // orange (default goalkeeper)
  '#9B59B6', // purple
  '#1ABC9C', // teal
  '#FFFFFF', // white
];

/** Find a role definition by id; falls back to the first default role. */
export function roleForId(id: string, custom?: PlayerRoleDef[]): PlayerRoleDef {
  const all = [...DEFAULT_ROLES, ...(custom ?? [])];
  return all.find((r) => r.id === id) ?? all[0];
}
